import React, { useState } from 'react'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { useAuth } from '../contexts/AuthContext'
import appleIcon from '../assets/images/apple-logo.svg'

const SignInForm = () => {
  const navigate = useNavigate()
  const { login, isLoading, error, clearError } = useAuth()
  const [formData, setFormData] = useState({
    emailOrPhone: '',
    password: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const validate = () => {
    const newErrors = {}

    if (!formData.emailOrPhone.trim()) {
      newErrors.emailOrPhone = 'Please enter your email or phone'
    } else if (
      !/^\S+@\S+\.\S+$/.test(formData.emailOrPhone) &&
      !/^\d{10,15}$/.test(formData.emailOrPhone)
    ) {
      newErrors.emailOrPhone = 'Enter a valid email or phone'
    }
    if (!formData.password) {
      newErrors.password = 'Please enter your password'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    }

    return newErrors
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
    setErrors({ ...errors, [e.target.name]: '' })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    // Clear any previous auth errors
    clearError()
    setIsSubmitting(true)

    try {
      // Use the auth service to login
      const result = await login(formData.emailOrPhone, formData.password)

      if (result.success) {
        toast.success(result.message || 'Signed in successfully!')
        navigate('/')
      } else {
        toast.error(result.error || 'Login failed')
        setErrors({ general: result.error })
      }
    } catch (error) {
      console.error('Signin failed', error)
      toast.error('An unexpected error occurred')
      setErrors({ general: 'An unexpected error occurred' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const inputClass = (hasError) =>
    `w-full border rounded-lg px-4 py-3 text-sm text-[#2c3e50] focus:outline-none transition-colors ${
      hasError ? 'border-[#e74c3c]' : 'border-[#e9ecef] focus:border-[#3498db]'
    }`

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center p-4">
      <div className="bg-white border border-[#e9ecef] rounded-xl shadow-sm p-10 w-full max-w-[440px]">
        {/* Brand */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-9 h-9 bg-[#2c3e50] rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <span className="text-xl font-bold text-[#2c3e50]">Admin Panel</span>
        </div>

        <h2 className="text-[1.6rem] font-semibold text-[#2c3e50] text-center mb-1">Sign In</h2>
        <p className="text-center text-[#7f8c8d] text-sm mb-8">Access your admin dashboard</p>

        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          {(errors.general || error) && (
            <div className="bg-[#fdedec] border border-[#e74c3c]/30 text-[#e74c3c] px-4 py-3 rounded-lg text-sm">
              {errors.general || error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">
              Email
            </label>
            <input
              type="text"
              name="emailOrPhone"
              value={formData.emailOrPhone}
              onChange={handleChange}
              className={inputClass(errors.emailOrPhone)}
              placeholder="admin@example.com"
            />
            {errors.emailOrPhone && <p className="text-[#e74c3c] text-xs mt-1">{errors.emailOrPhone}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={inputClass(errors.password)}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7f8c8d] hover:text-[#2c3e50]"
              >
                {showPassword ? <FaEye size={15} /> : <FaEyeSlash size={15} />}
              </button>
            </div>
            {errors.password && <p className="text-[#e74c3c] text-xs mt-1">{errors.password}</p>}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#2c3e50] hover:bg-[#34495e] text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-2"
          >
            {isSubmitting ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default SignInForm
